import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import { DataTable, EmptyState, Notice, PageHeader, useNotify, type DataTableColumn } from "@routes-plan/ui";
import HeaderResponsive from "../../../components/layouts/HeaderResponsive";
import PermissionButton from "../../../components/common/PermissionButton";
import { useResourcePermissions } from "../../../hooks/useResourcePermissions";
import { useT } from "../../../i18n";
import { setPageTitle } from "../../../utils/pageTitle";
import { Section } from "../components/ProcessFilters";

interface ShowAlerts {
  showAlert: (message: string, severity?: "success" | "info" | "warning" | "error") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type RouteRow = {
  id: number;
  name: string;
  officeId: number | null;
  officeName: string;
  alertActive: boolean;
  routeDisabled: boolean;
};

const MENU_ROUTE = "/processes/app-notices";

const AppNotices: React.FC<ShowAlerts> = ({ showLoading, hideLoading }) => {
  const t = useT();
  const notify = useNotify();
  const showAlert = useCallback(
    (message: string, severity: "success" | "info" | "warning" | "error" = "info") => notify[severity](message),
    [notify],
  );
  useEffect(() => {
    setPageTitle("appNotices.title");
  }, []);
  const { canRead, canUpdate } = useResourcePermissions(MENU_ROUTE);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [durationSeconds, setDurationSeconds] = useState(5);
  const [routes, setRoutes] = useState<RouteRow[]>([]);
  const [notAvailable, setNotAvailable] = useState(false);

  const load = useCallback(async () => {
    showLoading();
    try {
      const { data } = await axios.get("app-notices");
      const tpl = data?.data?.template;
      const rows: RouteRow[] = data?.data?.routes || [];
      setTitle(String(tpl?.title ?? ""));
      setBody(String(tpl?.body ?? ""));
      setDurationSeconds(Number(tpl?.durationSeconds) || 5);
      setRoutes(rows);
      setNotAvailable(false);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setNotAvailable(true);
        showAlert(t("appNotices.apiNotFound"), "info");
      } else {
        showAlert(t("appNotices.loadError"), "error");
      }
    } finally {
      hideLoading();
    }
  }, [hideLoading, showAlert, showLoading, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const setRouteActive = useCallback((id: number, next: boolean) => {
    setRoutes((prev) =>
      prev.map((row) => (row.id === id ? { ...row, alertActive: next } : row)),
    );
  }, []);

  const setRouteDisabledFlag = useCallback((id: number, next: boolean) => {
    setRoutes((prev) =>
      prev.map((row) => (row.id === id ? { ...row, routeDisabled: next } : row)),
    );
  }, []);

  const save = async () => {
    if (!canUpdate) {
      showAlert(t("appNotices.noPermissionSaveAlert"), "warning");
      return;
    }
    showLoading();
    try {
      await axios.put("app-notices", {
        template: {
          title,
          body,
          durationSeconds,
        },
        routes: routes.map((route) => ({
          id: route.id,
          alertActive: route.alertActive,
          routeDisabled: route.routeDisabled,
        })),
      });
      showAlert(t("appNotices.saved"), "success");
      await load();
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        showAlert(t("appNotices.apiNotFound"), "warning");
      } else {
        showAlert(t("appNotices.saveError"), "error");
      }
    } finally {
      hideLoading();
    }
  };

  const columns: DataTableColumn<RouteRow>[] = [
    { id: "name", header: t("appNotices.colRoute"), value: (r) => r.name, sortable: true },
    { id: "officeName", header: t("common.office"), value: (r) => r.officeName, sortable: true, hideBelow: "sm" },
    {
      id: "alertActive",
      header: t("appNotices.colNoticeActive"),
      value: (r) => (r.alertActive ? t("common.yes") : t("common.no")),
      align: "center",
      render: (r) => (
        <Switch
          checked={Boolean(r.alertActive)}
          disabled={!canUpdate}
          onChange={(_, checked) => setRouteActive(r.id, checked)}
          inputProps={{ "aria-label": `${t("appNotices.colNoticeActive")} ${r.name}` }}
        />
      ),
    },
    {
      id: "routeDisabled",
      header: t("appNotices.colDisableRoute"),
      value: (r) => (r.routeDisabled ? t("common.yes") : t("common.no")),
      align: "center",
      render: (r) => (
        <Switch
          color="error"
          checked={Boolean(r.routeDisabled)}
          disabled={!canUpdate}
          onChange={(_, checked) => setRouteDisabledFlag(r.id, checked)}
          inputProps={{ "aria-label": `${t("appNotices.colDisableRoute")} ${r.name}` }}
        />
      ),
    },
  ];

  const activeCount = routes.filter((r) => r.alertActive).length;

  return (
    <HeaderResponsive>
      <PageHeader
        title={t("appNotices.title")}
        description={t("appNotices.intro")}
        actions={
          <PermissionButton
            allowed={canUpdate}
            deniedTooltip={t("appNotices.noPermissionSave")}
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            onClick={() => void save()}
            disabled={!canRead || notAvailable}
          >
            {t("common.save")}
          </PermissionButton>
        }
      />

      {!canRead ? (
        <Notice severity="warning">{t("appNotices.noPermissionView")}</Notice>
      ) : notAvailable ? (
        <Notice severity="info">{t("appNotices.notAvailable")}</Notice>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 2fr) minmax(0, 3fr)" },
            alignItems: "start",
          }}
        >
          <Section title={t("appNotices.messageSection")}>
            <Box sx={{ display: "grid", gap: 2 }}>
              <TextField
                id="app-notice-title"
                label={t("appNotices.noticeTitle")}
                fullWidth
                value={title}
                disabled={!canUpdate}
                onChange={(e) => setTitle(e.target.value)}
              />
              <TextField
                id="app-notice-body"
                label={t("appNotices.noticeMessage")}
                fullWidth
                multiline
                minRows={4}
                value={body}
                disabled={!canUpdate}
                onChange={(e) => setBody(e.target.value)}
              />
              <TextField
                id="app-notice-seconds"
                label={t("appNotices.durationSeconds")}
                type="number"
                fullWidth
                value={durationSeconds}
                disabled={!canUpdate}
                inputProps={{ min: 1, max: 120 }}
                onChange={(e) => setDurationSeconds(Math.max(1, Math.min(120, Number(e.target.value) || 5)))}
              />
            </Box>
          </Section>

          <Section
            title={t("appNotices.routes")}
            action={
              routes.length > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("appNotices.activeCount", { count: activeCount, total: routes.length })}
                </Typography>
              ) : undefined
            }
          >
            <DataTable<RouteRow>
              rows={routes}
              columns={columns}
              getRowId={(r) => r.id}
              searchable
              ariaLabel={t("appNotices.routes")}
              maxHeight="min(60vh, 600px)"
              initialPageSize={20}
              emptyState={<EmptyState dense icon={<RouteOutlinedIcon />} title={t("appNotices.noRoutes")} />}
            />
          </Section>
        </Box>
      )}
    </HeaderResponsive>
  );
};

export default AppNotices;
